/**
 * OnboardingAIArenaTeaser
 *
 * Screen 3 of the onboarding flow.
 * Shows a multi-line YTD performance chart + compact legend with AI portfolio returns vs SPY.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { VictoryChart, VictoryLine, VictoryAxis, VictoryContainer } from 'victory';
import { colors, fontStyles, spacing, calculateChangeColor } from '../../../../styles';
import {
  getInvestingArenaPortfolios,
  getInvestingArenaChartData,
  InvestingArenaPortfolio,
  AIModelId,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
  ChartDataPoint,
} from '../../../../apis/investingArena';
import { getBasicInvestment } from '../../../../apis/backend';
import BottomSheet from '../../../BottomSheet';

const AI_MODEL_ORDER: AIModelId[] = ['openai', 'gemini', 'claude'];

// Skeleton pulse animation
const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// ─── Onboarding Performance Chart ─────────────────────────────────────────────

interface PerformanceChartProps {
  portfolios?: InvestingArenaPortfolio[];
}

const OnboardingPerformanceChart: React.FC<PerformanceChartProps> = ({ portfolios }) => {
  const { data: chartData, isLoading } = useQuery(
    ['investing-arena', 'chart', 'YTD'],
    () => getInvestingArenaChartData('YTD'),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 0,
    },
  );

  type TransformedPoint = { x: number; y: number; date: string };

  const transformedData = useMemo(() => {
    const empty: Record<AIModelId, TransformedPoint[]> = { claude: [], openai: [], gemini: [] };
    if (!chartData) return empty;
    AI_MODEL_ORDER.forEach((modelId) => {
      const modelData: ChartDataPoint[] = chartData[modelId] || [];
      empty[modelId] = modelData.map((point, index) => ({
        x: index,
        y: point.value,
        date: new Date(point.time * 1000).toISOString().slice(0, 10),
      }));
    });
    return empty;
  }, [chartData]);

  const { minY, maxY, dataLength } = useMemo(() => {
    const allValues: number[] = [];
    let maxLen = 0;
    Object.values(transformedData).forEach((data) => {
      if (data && data.length > 0) {
        data.forEach((p) => allValues.push(p.y));
        maxLen = Math.max(maxLen, data.length);
      }
    });
    if (allValues.length === 0) return { minY: -5, maxY: 10, dataLength: 0 };
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.12 || 5;
    return { minY: min - padding, maxY: max + padding, dataLength: maxLen };
  }, [transformedData]);

  const hasData = Object.values(transformedData).some((d) => d && d.length > 0);

  // Container ref for measuring width (needed for precise pill positioning)
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [hasData]);

  // Skeleton while loading
  if (isLoading) {
    return (
      <div
        css={css`
          height: 160px;
          border-radius: 8px;
          background: ${colors.cloud};
          ${skeletonPulse}
          html[data-theme='dark'] & {
            background: ${colors.tungsten};
          }
        `}
        data-testid="onboarding-chart-skeleton"
      />
    );
  }

  if (!hasData) {
    return null;
  }

  // Chart layout constants
  const chartHeight = 160;
  const chartPadding = { top: 8, bottom: 28, left: 44, right: 80 };
  const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;

  // Baseline data for y=0 dashed line
  const baselineData =
    dataLength > 0
      ? [
          { x: 0, y: 0 },
          { x: dataLength - 1, y: 0 },
        ]
      : [];

  // X-axis tick formatter: show start and end dates like "Dec 2" / "Mar 2"
  const xTickFormat = (x: number) => {
    const data =
      (transformedData.openai.length > 0 && transformedData.openai) ||
      (transformedData.claude.length > 0 && transformedData.claude) ||
      (transformedData.gemini.length > 0 && transformedData.gemini);
    const index = Math.round(x);
    if (!data || !data[index] || !data[index].date) return '';
    try {
      const date = new Date(data[index].date + 'T00:00:00');
      if (isNaN(date.getTime())) return '';
      return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`;
    } catch {
      return '';
    }
  };

  // Calculate pill positions from last data points, resolving overlaps
  const pillPositions = (() => {
    const pills = AI_MODEL_ORDER.map((modelId) => {
      const data = transformedData[modelId];
      if (!data || data.length === 0) return null;
      const lastPoint = data[data.length - 1];
      const yFraction = (maxY - lastPoint.y) / (maxY - minY);
      const yPixel = chartPadding.top + yFraction * plotHeight;
      const portfolio = portfolios?.find((p) => p.id === modelId);
      const returnPct = portfolio ? portfolio.return_pct * 100 : lastPoint.y;
      return { modelId, yPixel, returnPct };
    }).filter(Boolean) as { modelId: AIModelId; yPixel: number; returnPct: number }[];

    // Resolve overlapping pills — push apart if too close
    const minGap = 26;
    const sorted = [...pills].sort((a, b) => a.yPixel - b.yPixel);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].yPixel - sorted[i - 1].yPixel < minGap) {
        sorted[i] = { ...sorted[i], yPixel: sorted[i - 1].yPixel + minGap };
      }
    }
    return sorted;
  })();

  return (
    <div data-testid="onboarding-performance-chart">
      {/* Top legend row — icon + name + return % */}
      {portfolios && portfolios.length > 0 && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: ${spacing.deci};
            margin-bottom: 4px;
          `}
        >
          {AI_MODEL_ORDER.map((modelId) => {
            const p = portfolios.find((port) => port.id === modelId);
            if (!p) return null;
            const returnPct = p.return_pct * 100;
            const returnColor = calculateChangeColor(p.return_pct);
            return (
              <div
                key={modelId}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <img
                  src={AI_MODEL_ICONS[modelId]}
                  alt=""
                  css={css`
                    width: 14px;
                    height: 14px;
                    html[data-theme='dark'] & {
                      filter: ${modelId === 'openai' ? 'invert(1)' : 'none'};
                    }
                  `}
                />
                <span
                  css={css`
                    ${fontStyles.flea}
                    color: ${AI_MODEL_COLORS[modelId]};
                    font-weight: 500;
                    html[data-theme='dark'] & {
                      color: ${modelId === 'openai' ? colors.snow : AI_MODEL_COLORS[modelId]};
                    }
                  `}
                >
                  {AI_MODEL_NAMES[modelId]}
                </span>
                <span
                  css={css`
                    ${fontStyles.flea}
                    color: ${returnColor};
                    font-weight: 600;
                  `}
                >
                  {returnPct >= 0 ? '+' : ''}
                  {returnPct.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Chart with inline pill labels at endpoints */}
      <div
        ref={containerRef}
        css={css`
          position: relative;
          height: ${chartHeight}px;
          margin: 0 -${spacing.milli};
        `}
      >
        {containerWidth > 0 && (
          <VictoryChart
            width={containerWidth}
            height={chartHeight}
            padding={chartPadding}
            containerComponent={<VictoryContainer responsive={false} />}
            domain={dataLength > 0 ? { x: [0, dataLength - 1], y: [minY, maxY] } : undefined}
          >
            {/* Y-axis */}
            <VictoryAxis
              dependentAxis
              tickFormat={(t: number) => `${t.toFixed(0)}%`}
              tickCount={4}
              style={{
                axis: { stroke: 'transparent' },
                grid: { stroke: colors.cloud, strokeDasharray: '4,4', opacity: 0.5 },
                tickLabels: {
                  fill: colors.silver,
                  fontSize: 9,
                  fontFamily: 'HelveticaNowText, sans-serif',
                },
              }}
            />

            {/* X-axis — show only start and end dates */}
            <VictoryAxis
              tickFormat={xTickFormat}
              tickValues={dataLength > 1 ? [0, dataLength - 1] : undefined}
              style={{
                axis: { stroke: colors.cloud },
                tickLabels: {
                  fill: colors.silver,
                  fontSize: 9,
                  fontFamily: 'HelveticaNowText, sans-serif',
                },
              }}
            />

            {/* Dashed baseline at y=0 */}
            {baselineData.length > 0 && (
              <VictoryLine
                data={baselineData}
                style={{
                  data: {
                    stroke: colors.silver,
                    strokeWidth: 1,
                    strokeDasharray: '4,4',
                    opacity: 0.5,
                  },
                }}
              />
            )}

            {/* Lines for each AI model */}
            {AI_MODEL_ORDER.map((modelId) => {
              const data = transformedData[modelId];
              if (!data || data.length === 0) return null;
              return (
                <VictoryLine
                  key={modelId}
                  data={data}
                  interpolation="monotoneX"
                  style={{
                    data: {
                      stroke: AI_MODEL_COLORS[modelId],
                      strokeWidth: 2,
                    },
                  }}
                />
              );
            })}
          </VictoryChart>
        )}

        {/* Inline pill labels at line endpoints — HTML overlays with icons */}
        {containerWidth > 0 &&
          pillPositions.map((pill) => (
            <div
              key={pill.modelId}
              css={css`
                position: absolute;
                right: 4px;
                top: ${pill.yPixel}px;
                transform: translateY(-50%);
                display: flex;
                align-items: center;
                gap: 3px;
                background: ${AI_MODEL_COLORS[pill.modelId]};
                padding: 3px 8px 3px 3px;
                border-radius: 14px;
              `}
            >
              <div
                css={css`
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: rgba(255, 255, 255, 0.25);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                `}
              >
                <img
                  src={AI_MODEL_ICONS[pill.modelId]}
                  alt=""
                  css={css`
                    width: 13px;
                    height: 13px;
                    filter: brightness(0) invert(1);
                  `}
                />
              </div>
              <span
                css={css`
                  color: white;
                  font-size: 11px;
                  font-weight: 600;
                  font-family: 'HelveticaNowText', sans-serif;
                  line-height: 1;
                `}
              >
                {pill.returnPct >= 0 ? '+' : ''}
                {pill.returnPct.toFixed(0)}%
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const OnboardingAIArenaTeaser: React.FC = () => {
  const { t } = useTranslation();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // Fetch AI portfolio data (same query key as PerformanceCards — shared cache)
  const { data: portfolios, isError: portfoliosError } = useQuery(
    ['investing-arena', 'portfolios'],
    getInvestingArenaPortfolios,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 0,
    },
  );

  // Fetch SPY price_info for YTD return — changePercentYtd is a decimal fraction (e.g. 0.0542 = 5.42%)
  const { data: spyData, isLoading: spyLoading } = useQuery(
    ['basic-investment', 'SPY'],
    () => getBasicInvestment('SPY'),
    {
      staleTime: 10 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      retry: 0,
    },
  );

  if (portfoliosError) {
    return <div data-testid="ai-arena-error" />;
  }

  const spyYtd = spyData?.changePercentYtd ?? null;
  const spyColor =
    spyYtd === null || Math.abs(spyYtd * 100) < 0.05 ? colors.silver : spyYtd >= 0 ? '#4CAF50' : '#F44336';

  // Sort portfolios by preferred display order
  const sortedPortfolios = portfolios
    ? [...portfolios].sort((a, b) => AI_MODEL_ORDER.indexOf(a.id) - AI_MODEL_ORDER.indexOf(b.id))
    : [];

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
        margin-top: ${spacing.milli};
      `}
    >
      {/* Performance Chart with inline legend + pill labels */}
      <OnboardingPerformanceChart portfolios={sortedPortfolios.length > 0 ? sortedPortfolios : undefined} />

      {/* S&P 500 YTD comparison — minimal inline row */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
        `}
        data-testid="spy-ytd-row"
      >
        <span
          css={css`
            ${fontStyles.flea}
            color: ${colors.silver};
          `}
        >
          {t('onboarding.aiArenaSPY')}
        </span>
        <span
          css={css`
            ${fontStyles.flea}
            color: ${spyColor};
            font-weight: 600;
          `}
          data-testid="spy-ytd-value"
        >
          {spyLoading
            ? '—'
            : spyYtd === null
              ? '—'
              : (() => {
                  const pct = spyYtd * 100;
                  const formatted = Math.abs(pct).toFixed(1);
                  const sign = pct >= 0.05 ? '+' : pct <= -0.05 ? '-' : '';
                  return `${sign}${formatted}%`;
                })()}
        </span>
      </div>

      {/* Disclaimer link */}
      <button
        onClick={() => setDisclaimerOpen(true)}
        css={css`
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          text-align: center;
          width: 100%;
        `}
        data-testid="disclaimer-button"
      >
        <span
          css={css`
            ${fontStyles.flea}
            color: ${colors.silver};
          `}
        >
          {t('onboarding.aiArenaDisclaimer')}
        </span>
      </button>

      {/* Disclaimer bottom sheet */}
      <BottomSheet
        isOpen={disclaimerOpen}
        title={t('onboarding.aiArenaDisclaimerTitle')}
        onClose={() => setDisclaimerOpen(false)}
        headerStyle="left-aligned"
      >
        <div
          css={css`
            padding: ${spacing.deci};
          `}
          data-testid="disclaimer-content"
        >
          <p
            css={css`
              ${fontStyles.dog}
              color: ${colors.liquorice};
              line-height: 1.6;
              margin: 0;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {t('onboarding.aiArenaDisclaimerContent')}
          </p>
        </div>
      </BottomSheet>
    </div>
  );
};

export default OnboardingAIArenaTeaser;
